import * as React from 'react'
import { inject, observer } from 'mobx-react'
import { message } from 'antd'
import { WeedmapApi, WeedmapModels } from '@getgreenline/weedmaps'
// eslint-disable-next-line import/no-extraneous-dependencies
import { debounce } from 'lodash'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { IntegrationStore, Integrators, IntegrationLocAPIKey } from '../IntegrationStore'
import { IntegrationApiSettings } from '../IntegrationComponents/IntegrationApiSettings'
import { parseErrorMsg } from '../../../../utilities/helpers'
import { HelpLinks } from '../../../../constants/ExternalLinks'

interface Props {
  integrationStore: IntegrationStore<Integrators.WEEDMAP>
  loadWeedmapLocationApiKeys: () => void
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

@inject('currentCompanyStore')
@observer
export class WeedmapsSettings extends React.Component<Props & InjectedProps> {
  MESSAGE_KEY = 'weedmapsSettingsMessage'

  authToken = window.localStorage.getItem('authToken') || ''

  componentDidMount() {
    const { currentCompanyStore } = this.props
    currentCompanyStore!.productStore!.getCategories()
  }

  onInputChange = debounce(
    async (location: IntegrationLocAPIKey<Integrators.WEEDMAP>, value: string) => {
      if (value === '') {
        location.setCanSave(true)
        return location.setUpdatedApiKey(null)
      }

      location.setUpdatedApiKey(value)
      try {
        location.setIsUpdating(true)
        const valid = await WeedmapApi.validateWeedMapId(
          this.props.currentCompanyStore!.company!.id,
          value,
          this.authToken,
        )
        location.setCanSave(valid)
      } catch (err) {
        location.setCanSave(false)
      } finally {
        location.setIsUpdating(false)
      }
    },
    100,
  )

  saveApiKey = async (locationItem: IntegrationLocAPIKey<Integrators.WEEDMAP>) => {
    if (locationItem.isUpdating === true) {
      return
    }

    try {
      locationItem.setIsUpdating(true)
      const locationContract = new WeedmapModels.LocationContract({
        locationId: locationItem.location.id,
        wmid: locationItem.updatedApiKey ? parseInt(locationItem.updatedApiKey, 10) : null,
      })

      await WeedmapApi.upsertLocation(
        locationContract,
        this.props.currentCompanyStore!.company!.id,
        this.authToken,
      )
      await this.props.loadWeedmapLocationApiKeys()

      message.success({
        content: `API key updated for ${locationItem.location.name}`,
        key: this.MESSAGE_KEY,
      })
    } catch (error) {
      message.error({
        content: parseErrorMsg(
          error || `An error occurred while updating API key for ${locationItem.location.name}`,
        ),
        key: this.MESSAGE_KEY,
      })
    } finally {
      locationItem.setIsUpdating(false)
    }
  }

  render() {
    const { integrationStore, currentCompanyStore } = this.props
    return (
      <IntegrationApiSettings
        integrationStore={integrationStore}
        onItemSave={this.saveApiKey}
        onInputChange={this.onInputChange}
        description={
          <div className='alert alert-warning'>
            Each location requires its own WMID.
            <br />
            <a href={HelpLinks.WEEDMAPS} target='_blank' rel='noreferrer'>
              Get your WMID from Weedmaps
            </a>
          </div>
        }
        apiKeyTitle='WMID (WeedMaps ID)'
      />
    )
  }
}
